<template>
  <b-modal
    v-model="modalVisible"
    size="xl"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
    modal-class="mapping-manager-shell"
    content-class="mapping-manager-modal"
    body-class="mapping-manager-body"
    @show="handleShow"
    @hidden="handleHidden"
  >
    <div class="manager-layout">
      <header class="manager-header">
        <div>
          <span class="manager-eyebrow">Dynamic configuration</span>
          <h3>Room Mapping Data Management</h3>
          <p>Review and maintain the live rules used by Room Mapping.</p>
        </div>
        <button type="button" class="manager-close" title="Close" @click="closeManager">
          <b-icon icon="x" />
        </button>
      </header>

      <div class="manager-toolbar">
        <label class="manager-search">
          <b-icon icon="search" />
          <input
            v-model.trim="filters.search"
            type="search"
            placeholder="Search values, aliases or match rules"
            aria-label="Search mappings"
          />
        </label>
        <select v-model="filters.category" class="manager-select" aria-label="Category" @change="selectCategory(filters.category)">
          <option v-for="category in categories" :key="category.key" :value="category.key">
            {{ category.label }}
          </option>
        </select>
        <select v-model="filters.status" class="manager-select status-select" aria-label="Status" @change="applyFilters">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button type="button" class="add-mapping-btn" @click="openCreateForm">
          <b-icon icon="plus-lg" />
          Add New Mapping
        </button>
      </div>

      <nav class="category-tabs" aria-label="Mapping categories">
        <button
          v-for="category in categories"
          :key="category.key"
          type="button"
          :class="{ active: filters.category === category.key }"
          @click="selectCategory(category.key)"
        >
          {{ category.label }}
        </button>
      </nav>

      <div v-if="loading" class="mapping-skeleton" aria-label="Loading mappings">
        <div v-for="row in 7" :key="row" class="mapping-skeleton-row">
          <span v-for="cell in 6" :key="cell"></span>
        </div>
      </div>

      <div v-else-if="errorMessage" class="manager-state error-state">
        <b-icon icon="exclamation-circle" />
        <h4>Mappings could not be loaded</h4>
        <p>{{ errorMessage }}</p>
        <button type="button" @click="fetchMappings">Try Again</button>
      </div>

      <div v-else-if="!mappings.length" class="manager-state empty-state">
        <b-icon icon="inbox" />
        <h4>No mappings found</h4>
        <p>Try another filter or add the first mapping for this category.</p>
        <button type="button" @click="openCreateForm">Add New Mapping</button>
      </div>

      <template v-else>
        <div class="mapping-table-scroll">
          <table class="mapping-table">
            <thead>
              <tr>
                <th>Standard Value</th>
                <th>Translations</th>
                <th>Aliases</th>
                <th>Match Any</th>
                <th>Match All</th>
                <th>Match None</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th class="actions-heading">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mapping in mappings" :key="mapping.id">
                <td class="standard-cell" :title="mapping.standard_value">
                  <strong>{{ mapping.standard_value }}</strong>
                  <small>#{{ String(mapping.id).slice(0, 8) }}</small>
                </td>
                <td><mapping-tags :items="translationTags(mapping)" :expanded="isExpanded(mapping.id, 'translations')" @toggle="toggleExpanded(mapping.id, 'translations')" /></td>
                <td><mapping-tags :items="mapping.aliases" :expanded="isExpanded(mapping.id, 'aliases')" @toggle="toggleExpanded(mapping.id, 'aliases')" /></td>
                <td><mapping-tags :items="mapping.match_any" :expanded="isExpanded(mapping.id, 'match_any')" @toggle="toggleExpanded(mapping.id, 'match_any')" /></td>
                <td><mapping-tags :items="mapping.match_all" :expanded="isExpanded(mapping.id, 'match_all')" @toggle="toggleExpanded(mapping.id, 'match_all')" /></td>
                <td><mapping-tags :items="mapping.match_none" tone="danger" :expanded="isExpanded(mapping.id, 'match_none')" @toggle="toggleExpanded(mapping.id, 'match_none')" /></td>
                <td><span class="priority-pill">{{ mapping.priority }}</span></td>
                <td>
                  <button
                    type="button"
                    class="status-toggle"
                    :class="{ active: Boolean(Number(mapping.is_active)) }"
                    :disabled="statusBusyId === mapping.id"
                    :title="Number(mapping.is_active) ? 'Disable mapping' : 'Enable mapping'"
                    @click="toggleStatus(mapping)"
                  >
                    <b-spinner v-if="statusBusyId === mapping.id" small />
                    <template v-else>{{ Number(mapping.is_active) ? 'Active' : 'Inactive' }}</template>
                  </button>
                </td>
                <td class="updated-cell">
                  <span>{{ formatDate(mapping.updated_at) }}</span>
                  <small v-if="mapping.updated_by">by {{ mapping.updated_by }}</small>
                </td>
                <td>
                  <div class="row-actions">
                    <button type="button" title="Edit mapping" @click="openEditForm(mapping)">
                      <b-icon icon="pencil" />
                    </button>
                    <button type="button" class="delete-action" title="Delete mapping" @click="confirmDelete(mapping)">
                      <b-icon icon="trash" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer class="manager-pagination">
          <span>
            Showing {{ pageStart }}–{{ pageEnd }} of {{ pagination.total }}
          </span>
          <div>
            <select v-model.number="pagination.per_page" aria-label="Rows per page" @change="changePageSize">
              <option :value="10">10 / page</option>
              <option :value="20">20 / page</option>
              <option :value="50">50 / page</option>
            </select>
            <button type="button" :disabled="pagination.page <= 1" @click="changePage(pagination.page - 1)">
              <b-icon icon="chevron-left" />
            </button>
            <span>Page {{ pagination.page }} of {{ pagination.total_pages }}</span>
            <button type="button" :disabled="pagination.page >= pagination.total_pages" @click="changePage(pagination.page + 1)">
              <b-icon icon="chevron-right" />
            </button>
          </div>
        </footer>
      </template>

      <div v-if="showForm" class="mapping-form-backdrop" @click.self="closeForm">
        <aside class="mapping-form-panel" role="dialog" aria-modal="true" :aria-label="formTitle">
          <header>
            <div>
              <span>{{ editingId ? 'Update rule' : 'Create rule' }}</span>
              <h4>{{ formTitle }}</h4>
            </div>
            <button type="button" title="Close form" :disabled="saving" @click="closeForm">
              <b-icon icon="x" />
            </button>
          </header>

          <form @submit.prevent="saveMapping">
            <div v-if="formError" class="form-error">
              <b-icon icon="exclamation-circle" />
              {{ formError }}
            </div>

            <label class="form-field">
              <span>Category <em>*</em></span>
              <select v-model="form.category" :disabled="Boolean(editingId)">
                <option value="" disabled>Select category</option>
                <option v-for="category in categories" :key="category.key" :value="category.key">{{ category.label }}</option>
              </select>
              <small v-if="fieldErrors.category" class="field-error">{{ fieldErrors.category }}</small>
            </label>

            <label class="form-field">
              <span>Standard Value <em>*</em></span>
              <input v-model.trim="form.standard_value" type="text" maxlength="255" placeholder="e.g. Deluxe Room" />
              <small v-if="fieldErrors.standard_value" class="field-error">{{ fieldErrors.standard_value }}</small>
            </label>

            <div class="form-field">
              <span>Translations</span>
              <div v-for="language in translationLanguages" :key="language" class="translation-row">
                <strong>{{ language.toUpperCase() }}</strong>
                <input v-model.trim="form.translations[language]" type="text" maxlength="500" :placeholder="`${language.toUpperCase()} translation`" />
              </div>
              <small v-if="fieldErrors.translations" class="field-error">{{ fieldErrors.translations }}</small>            </div>

            <div v-for="field in tagFields" :key="field.key" class="form-field tag-form-field">
              <span>{{ field.label }}</span>
              <div class="tag-input" :class="{ focused: focusedTagField === field.key }">
                <span v-for="tag in form[field.key]" :key="`${field.key}-${tag}`" class="input-tag" :title="tag">
                  {{ tag }}
                  <button type="button" :aria-label="`Remove ${tag}`" @click="removeTag(field.key, tag)">×</button>
                </span>
                <input
                  v-model="tagDrafts[field.key]"
                  type="text"
                  :placeholder="form[field.key].length ? 'Add another…' : `Type ${field.label.toLowerCase()} and press Enter`"
                  @focus="focusedTagField = field.key"
                  @blur="handleTagBlur(field.key)"
                  @keydown="handleTagKeydown($event, field.key)"
                />
              </div>
              <small v-if="tagErrors[field.key]" class="field-error">{{ tagErrors[field.key] }}</small>
              <small v-else class="field-help">Press Enter after each value. Duplicate and empty tags are ignored.</small>
            </div>

            <div class="form-grid-two">
              <label class="form-field">
                <span>Priority</span>
                <input v-model.number="form.priority" type="number" step="1" />
                <small v-if="fieldErrors.priority" class="field-error">{{ fieldErrors.priority }}</small>
              </label>
              <label class="form-field status-field">
                <span>Status</span>
                <button type="button" class="form-status-switch" :class="{ active: form.is_active }" :aria-pressed="form.is_active" @click="form.is_active = !form.is_active">
                  <i></i>
                  {{ form.is_active ? 'Active' : 'Inactive' }}
                </button>
              </label>
            </div>

            <footer>
              <button type="button" class="cancel-btn" :disabled="saving" @click="closeForm">Cancel</button>
              <button type="submit" class="save-btn" :disabled="saving">
                <b-spinner v-if="saving" small />
                <b-icon v-else icon="check2" />
                {{ saving ? 'Saving…' : 'Save Mapping' }}
              </button>
            </footer>
          </form>
        </aside>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from '@axios'
import { BIcon, BModal, BSpinner } from 'bootstrap-vue'

const MappingTags = {
  props: {
    items: { type: Array, default: () => [] },
    expanded: { type: Boolean, default: false },
    tone: { type: String, default: 'default' },
  },
  computed: {
    visibleItems() {
      return this.expanded ? this.items : this.items.slice(0, 3)
    },
    remaining() {
      return Math.max(this.items.length - 3, 0)
    },
  },
  template: `<div>
    <div v-if="items.length" class="mapping-tags" :class="'tone-' + tone">
        <span v-for="item in visibleItems" :key="item" class="mapping-tag" :title="item">{{ item }}</span>
        <button v-if="remaining || expanded" type="button" class="more-tags" @click="$emit('toggle')">
          {{ expanded ? 'Show less' : '+' + remaining + ' more' }}
        </button>
    </div>
    <span v-else class="no-values">—</span>
  </div>`,
}

const TRANSLATION_LANGUAGES = ['ar', 'tr', 'es']

const emptyTranslations = translations => TRANSLATION_LANGUAGES.reduce((values, language) => {
  values[language] = String((translations || {})[language] || '')
  return values
}, {})

const emptyForm = category => ({
  category: category || 'room',
  standard_value: '',
  translations: emptyTranslations(),
  aliases: [],
  match_any: [],
  match_all: [],
  match_none: [],
  priority: 0,
  is_active: true,
})

export default {
  name: 'RoomMappingDataManager',
  components: { BIcon, BModal, BSpinner, MappingTags },
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      categories: [
        { key: 'room', label: 'Room Summary' },
        { key: 'room_type', label: 'Room Type' },
        { key: 'room_variant', label: 'Room Variant' },
        { key: 'bed', label: 'Bed Type' },
        { key: 'meal', label: 'Meal Plan' },
        { key: 'view', label: 'View' },
        { key: 'phrase', label: 'Phrase' },
      ],
      tagFields: [
        { key: 'aliases', label: 'Aliases' },
        { key: 'match_any', label: 'Match Any' },
        { key: 'match_all', label: 'Match All' },
        { key: 'match_none', label: 'Match None' },
      ],
      filters: { search: '', category: 'room', status: 'all' },
      pagination: { page: 1, per_page: 20, total: 0, total_pages: 1 },
      mappings: [],
      loading: false,
      errorMessage: '',
      showForm: false,
      editingId: '',
      form: emptyForm('room'),
      tagDrafts: { aliases: '', match_any: '', match_all: '', match_none: '' },
      tagErrors: {},
      focusedTagField: '',
      fieldErrors: {},
      formError: '',
      saving: false,
      statusBusyId: '',
      expandedCells: {},
      searchTimer: null,
    }
  },
  computed: {
    modalVisible: {
      get() { return this.value },
      set(value) { this.$emit('input', value) },
    },
    formTitle() {
      return this.editingId ? 'Edit Mapping' : 'Add New Mapping'
    },
    pageStart() {
      return this.pagination.total ? ((this.pagination.page - 1) * this.pagination.per_page) + 1 : 0
    },
    pageEnd() {
      return Math.min(this.pagination.page * this.pagination.per_page, this.pagination.total)
    },
    translationLanguages() {
      return TRANSLATION_LANGUAGES
    },
  },
  watch: {
    'filters.search'() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.pagination.page = 1
        this.fetchMappings()
      }, 350)
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    handleShow() {
      this.fetchMappings()
    },
    handleHidden() {
      this.closeForm()
      this.mappings = []
      this.errorMessage = ''
    },
    closeManager() {
      if (!this.saving) this.modalVisible = false
    },
    async fetchMappings() {
      if (!this.modalVisible) return
      this.loading = true
      this.errorMessage = ''
      try {
        const { data } = await axios.get('room_mapping_rules', {
          params: {
            page: this.pagination.page,
            per_page: this.pagination.per_page,
            search: this.filters.search,
            category: this.filters.category,
            status: this.filters.status,
          },
        })
        this.mappings = data.mappings || []
        this.pagination = { ...this.pagination, ...(data.pagination || {}) }
      } catch (error) {
        this.mappings = []
        this.errorMessage = this.apiMessage(error, 'Please check the connection and try again.')
      } finally {
        this.loading = false
      }
    },
    selectCategory(category) {
      this.filters.category = category
      this.pagination.page = 1
      this.expandedCells = {}
      this.fetchMappings()
    },
    applyFilters() {
      this.pagination.page = 1
      this.fetchMappings()
    },
    changePage(page) {
      this.pagination.page = page
      this.fetchMappings()
    },
    changePageSize() {
      this.pagination.page = 1
      this.fetchMappings()
    },
    isExpanded(id, field) {
      return Boolean(this.expandedCells[`${id}-${field}`])
    },
    toggleExpanded(id, field) {
      const key = `${id}-${field}`
      this.$set(this.expandedCells, key, !this.expandedCells[key])
    },
    openCreateForm() {
      this.editingId = ''
      this.form = emptyForm(this.filters.category)
      this.resetFormState()
      this.showForm = true
    },
    openEditForm(mapping) {
      this.editingId = mapping.id
      this.form = {
        category: mapping.category,
        standard_value: mapping.standard_value,
        translations: emptyTranslations(mapping.translations),
        aliases: [...(mapping.aliases || [])],
        match_any: [...(mapping.match_any || [])],
        match_all: [...(mapping.match_all || [])],
        match_none: [...(mapping.match_none || [])],
        priority: Number(mapping.priority) || 0,
        is_active: Boolean(Number(mapping.is_active)),
      }
      this.resetFormState()
      this.showForm = true
    },
    resetFormState() {
      this.formError = ''
      this.fieldErrors = {}
      this.tagErrors = {}
      this.tagDrafts = { aliases: '', match_any: '', match_all: '', match_none: '' }
    },
    closeForm() {
      if (this.saving) return
      this.showForm = false
      this.editingId = ''
      this.resetFormState()
    },
    normalized(value) {
      return String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase()
    },
    translationTags(mapping) {
      return Object.entries(mapping.translations || {}).map(([language, value]) => `${language.toUpperCase()}: ${value}`)
    },
    addTag(field) {
      const value = String(this.tagDrafts[field] || '').trim().replace(/\s+/g, ' ')
      this.$set(this.tagErrors, field, '')
      if (!value) return
      if (this.form[field].some(item => this.normalized(item) === this.normalized(value))) {
        this.$set(this.tagErrors, field, 'This value is already added.')
        return
      }
      this.form[field].push(value)
      this.$set(this.tagDrafts, field, '')
    },
    handleTagBlur(field) {
      this.focusedTagField = ''
      if (String(this.tagDrafts[field] || '').trim()) this.addTag(field)
    },
    removeTag(field, tag) {
      this.form[field] = this.form[field].filter(item => item !== tag)
      this.$set(this.tagErrors, field, '')
    },
    removeLastTag(field) {
      if (!this.tagDrafts[field] && this.form[field].length) this.form[field].pop()
    },
    handleTagKeydown(event, field) {
      if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault()
        this.addTag(field)
      } else if (event.key === 'Backspace') {
        this.removeLastTag(field)
      }
    },
    validateForm() {
      this.fieldErrors = {}
      if (!this.form.category) this.$set(this.fieldErrors, 'category', 'Category is required.')
      if (!this.form.standard_value.trim()) this.$set(this.fieldErrors, 'standard_value', 'Standard Value is required.')
      if (Object.values(this.form.translations || {}).some(value => !String(value || '').trim())) {
        this.$set(this.fieldErrors, 'translations', 'Enter a value for every integrated language.')
      }
      if (!Number.isInteger(Number(this.form.priority))) this.$set(this.fieldErrors, 'priority', 'Priority must be a whole number.')
      return !Object.keys(this.fieldErrors).length
    },
    async saveMapping() {
      this.tagFields.forEach(field => {
        if (String(this.tagDrafts[field.key] || '').trim()) this.addTag(field.key)
      })
      if (!this.validateForm()) return

      this.saving = true
      this.formError = ''
      try {
        const payload = {
          ...this.form,
          priority: Number(this.form.priority),
          is_active: Boolean(this.form.is_active),
        }
        const validation = await axios.post('room_mapping_rules/validate', {
          ...payload,
          exclude_id: this.editingId || undefined,
        })
        const aliasWarnings = validation.data.warnings || []
        if (aliasWarnings.length) {
          const warningResult = await this.$swal({
            title: 'Duplicate alias warning',
            html: aliasWarnings.map(message => `<div style="margin-bottom:6px">${this.escapeHtml(message)}</div>`).join(''),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Save anyway',
            cancelButtonText: 'Review aliases',
            confirmButtonColor: '#6d28d9',
            width: 500,
          })
          if (!warningResult.isConfirmed) return
        }
        const response = this.editingId
          ? await axios.patch(`room_mapping_rules/${this.editingId}`, payload)
          : await axios.post('room_mapping_rules', payload)
        this.closeFormAfterSave()
        await this.fetchMappings()
        this.toast(response.data.message || 'Mapping saved successfully.', 'success')
      } catch (error) {
        this.formError = this.apiMessage(error, 'Mapping could not be saved. Please try again.')
      } finally {
        this.saving = false
      }
    },
    closeFormAfterSave() {
      this.showForm = false
      this.editingId = ''
      this.resetFormState()
    },
    async toggleStatus(mapping) {
      if (this.statusBusyId) return
      this.statusBusyId = mapping.id
      try {
        const isActive = !Boolean(Number(mapping.is_active))
        const { data } = await axios.patch(`room_mapping_rules/${mapping.id}`, { is_active: isActive })
        await this.fetchMappings()
        this.toast(isActive ? 'Mapping enabled.' : 'Mapping disabled.', 'success')
        if (data.warnings && data.warnings.length) this.toast(data.warnings.join(' '), 'warning', 5000)
      } catch (error) {
        this.toast(this.apiMessage(error, 'Status could not be updated.'), 'error')
      } finally {
        this.statusBusyId = ''
      }
    },
    async confirmDelete(mapping) {
      const result = await this.$swal({
        title: 'Delete this mapping?',
        text: `“${mapping.standard_value}” will be soft-deleted and will no longer be used.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#6d28d9',
        width: 430,
      })
      if (!result.isConfirmed) return

      try {
        const { data } = await axios.delete(`room_mapping_rules/${mapping.id}`)
        if (this.mappings.length === 1 && this.pagination.page > 1) this.pagination.page -= 1
        await this.fetchMappings()
        this.toast(data.message || 'Mapping deleted successfully.', 'success')
      } catch (error) {
        this.toast(this.apiMessage(error, 'Mapping could not be deleted.'), 'error')
      }
    },
    formatDate(value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').replace(/\.\d+/, '').replace('Z', '')
    },
    apiMessage(error, fallback) {
      return (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) || fallback
    },
    escapeHtml(value) {
      return String(value).replace(/[&<>'"]/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;',
      }[character]))
    },
    toast(title, icon = 'success', timer = 2600) {
      this.$swal({
        toast: true,
        position: 'top-end',
        icon,
        title,
        showConfirmButton: false,
        timer,
        timerProgressBar: true,
        width: 360,
      })
    },
  },
}
</script>

<style scoped>
.manager-layout { min-height: 680px; color: #202039; background: #fff; }
.manager-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; padding: 24px 26px 18px; border-bottom: 1px solid #ebe8f4; }
.manager-eyebrow { display: block; margin-bottom: 5px; color: #7c3aed; font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; }
.manager-header h3 { margin: 0; color: #241b3f; font-size: 25px; font-weight: 800; }
.manager-header p { margin: 6px 0 0; color: #746d84; font-size: 13px; }
.manager-close, .mapping-form-panel header button { width: 38px; height: 38px; border: 1px solid #ddd7eb; border-radius: 10px; background: #fff; color: #655d73; font-size: 23px; display: inline-flex; align-items: center; justify-content: center; }
.manager-close:hover, .mapping-form-panel header button:hover { background: #f7f4fc; color: #6d28d9; }
.manager-toolbar { display: grid; grid-template-columns: minmax(280px, 1fr) 170px 150px auto; gap: 10px; padding: 16px 26px; }
.manager-search { height: 42px; margin: 0; padding: 0 13px; overflow: hidden; box-sizing: border-box; display: flex; align-items: center; gap: 9px; border: 1px solid #ded8eb; border-radius: 10px; background: #fff; color: #8a8298; }
.manager-search:focus-within { border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(124, 58, 237, .1); }
.manager-search input { width: 100%; min-width: 0; height: 100% !important; margin: 0; padding: 0; border: 0 !important; border-radius: 0 !important; outline: 0; background: transparent !important; box-shadow: none !important; color: #28223a; font-size: 13px; }
.manager-select, .manager-pagination select { height: 42px; padding: 0 34px 0 11px; border: 1px solid #ded8eb; border-radius: 10px; background: #fff; color: #3e3650; font-size: 13px; }
.add-mapping-btn { min-height: 42px; padding: 0 17px; border: 1px solid #6d28d9; border-radius: 10px; background: #6d28d9; color: #fff; font-size: 13px; font-weight: 750; display: inline-flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 8px 18px rgba(109, 40, 217, .2); }
.add-mapping-btn:hover { background: #5b21b6; }
.category-tabs { display: flex; gap: 5px; padding: 0 26px 12px; overflow-x: auto; border-bottom: 1px solid #ebe8f4; }
.category-tabs button { padding: 9px 14px; border: 0; border-radius: 9px; background: transparent; color: #746d84; font-size: 13px; font-weight: 700; white-space: nowrap; }
.category-tabs button:hover { background: #f7f4fc; color: #6d28d9; }
.category-tabs button.active { background: #f0e9ff; color: #6326c7; }
.mapping-table-scroll { max-height: 480px; overflow: auto; }
.mapping-table { width: 100%; min-width: 1340px; border-collapse: separate; border-spacing: 0; }
.mapping-table th { position: sticky; top: 0; z-index: 2; padding: 11px 12px; border-bottom: 1px solid #ded8eb; background: #f7f5fb; color: #655d73; font-size: 11px; font-weight: 800; letter-spacing: .025em; text-align: left; white-space: nowrap; }
.mapping-table td { max-width: 210px; padding: 11px 12px; border-bottom: 1px solid #efedf4; color: #373044; font-size: 12px; vertical-align: top; }
.mapping-table tbody tr:hover { background: #fbfaff; }
.standard-cell strong { display: block; max-width: 190px; overflow: hidden; color: #2a213a; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.standard-cell small, .updated-cell small { display: block; margin-top: 4px; color: #938ba1; font-size: 10px; }
.updated-cell { min-width: 145px; white-space: nowrap; }
.priority-pill { min-width: 32px; padding: 4px 8px; border-radius: 7px; background: #f1ecfa; color: #6233a5; font-weight: 800; text-align: center; display: inline-block; }
.status-toggle { min-width: 72px; min-height: 28px; padding: 4px 9px; border: 1px solid #e3dce9; border-radius: 999px; background: #f4f1f5; color: #7d7183; font-size: 11px; font-weight: 800; }
.status-toggle.active { border-color: #bfe7d0; background: #e5f8ed; color: #14804a; }
.row-actions { display: flex; gap: 6px; }
.row-actions button { width: 31px; height: 31px; border: 1px solid #ded8eb; border-radius: 8px; background: #fff; color: #6d28d9; display: inline-flex; align-items: center; justify-content: center; }
.row-actions button:hover { background: #f2ebff; }
.row-actions .delete-action { color: #c24157; }
.row-actions .delete-action:hover { border-color: #f0cbd2; background: #fff0f2; }
.manager-pagination { min-height: 60px; padding: 10px 26px; display: flex; align-items: center; justify-content: space-between; gap: 14px; border-top: 1px solid #ebe8f4; color: #756e81; font-size: 12px; }
.manager-pagination > div { display: flex; align-items: center; gap: 8px; }
.manager-pagination select { height: 34px; }
.manager-pagination button { width: 34px; height: 34px; border: 1px solid #ded8eb; border-radius: 8px; background: #fff; color: #6d28d9; }
.manager-pagination button:disabled { opacity: .45; }
.manager-state { min-height: 430px; padding: 40px; text-align: center; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.manager-state > .b-icon { margin-bottom: 12px; color: #8b5cf6; font-size: 40px; }
.manager-state h4 { margin: 0 0 6px; color: #302744; font-size: 18px; }
.manager-state p { max-width: 480px; margin: 0 0 16px; color: #7d7589; }
.manager-state button { padding: 8px 14px; border: 1px solid #6d28d9; border-radius: 8px; background: #fff; color: #6d28d9; font-weight: 700; }
.error-state > .b-icon { color: #c24157; }
.mapping-skeleton { padding: 14px 26px; }
.mapping-skeleton-row { display: grid; grid-template-columns: 1.2fr 2fr 1fr 1fr 1fr .7fr; gap: 12px; margin-bottom: 13px; }
.mapping-skeleton-row span { height: 29px; border-radius: 7px; background: linear-gradient(90deg, #f1eef6 25%, #faf9fc 50%, #f1eef6 75%); background-size: 200% 100%; animation: mapping-shimmer 1.3s infinite; }
.mapping-form-backdrop { position: fixed; z-index: 1065; inset: 0; background: rgba(31, 21, 49, .32); display: flex; justify-content: flex-end; }
.mapping-form-panel { width: min(560px, 94vw); height: 100%; overflow-y: auto; background: #fff; box-shadow: -18px 0 50px rgba(35, 20, 57, .2); }
.mapping-form-panel > header { position: sticky; top: 0; z-index: 2; padding: 20px 22px; border-bottom: 1px solid #ebe8f4; background: #fff; display: flex; align-items: flex-start; justify-content: space-between; }
.mapping-form-panel header span { color: #7c3aed; font-size: 11px; font-weight: 800; text-transform: uppercase; }
.mapping-form-panel h4 { margin: 3px 0 0; color: #2a203d; font-size: 21px; }
.mapping-form-panel form { padding: 20px 22px 28px; }
.form-field { margin: 0 0 17px; display: block; }
.form-field > span { display: block; margin-bottom: 7px; color: #4b4358; font-size: 12px; font-weight: 800; }
.form-field em { color: #c24157; font-style: normal; }
.form-field > input, .form-field > select { width: 100%; height: 42px; padding: 0 11px; border: 1px solid #dcd6e7; border-radius: 9px; outline: 0; color: #312a3d; font-size: 13px; }
.form-field > input:focus, .form-field > select:focus { border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(124, 58, 237, .09); }
.form-field > select:disabled { background: #f5f3f8; color: #837b8d; }
.form-field small { display: block; margin-top: 5px; color: #8b8396; font-size: 10px; }
.form-field .field-error { color: #b23a50; }
.form-field .field-help { color: #8b8396; }
.tag-input { min-height: 44px; padding: 6px 7px; border: 1px solid #dcd6e7; border-radius: 9px; display: flex; align-items: center; flex-wrap: wrap; gap: 5px; }
.tag-input.focused { border-color: #8b5cf6; box-shadow: 0 0 0 3px rgba(124, 58, 237, .09); }
.tag-input input { min-width: 170px; height: 28px; flex: 1; border: 0; outline: 0; font-size: 12px; }
.translation-row { display: grid; grid-template-columns: 42px 1fr; align-items: center; gap: 8px; margin-top: 7px; }
.translation-row strong { color: #6d28d9; font-size: 11px; }
.translation-row input { height: 38px; padding: 0 10px; border: 1px solid #dcd6e7; border-radius: 8px; font-size: 12px; }
.input-tag { max-width: 100%; padding: 4px 6px 4px 8px; border-radius: 6px; background: #f0e9ff; color: #5d25b2; font-size: 11px; display: inline-flex; align-items: center; gap: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.input-tag button { padding: 0; border: 0; background: transparent; color: #7d45c8; font-size: 16px; line-height: 1; }
.form-grid-two { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.status-field { display: flex; flex-direction: column; }
.form-status-switch { height: 42px; padding: 0 13px; border: 1px solid #dcd6e7; border-radius: 9px; background: #f5f3f6; color: #746a7c; font-size: 12px; font-weight: 800; display: flex; align-items: center; gap: 8px; }
.form-status-switch i { width: 9px; height: 9px; border-radius: 50%; background: #9b929f; }
.form-status-switch.active { border-color: #bce4cc; background: #ebf9f0; color: #147c48; }
.form-status-switch.active i { background: #25a765; }
.mapping-form-panel form > footer { position: sticky; bottom: -28px; margin: 8px -22px -28px; padding: 15px 22px; border-top: 1px solid #ebe8f4; background: #fff; display: flex; justify-content: flex-end; gap: 9px; }
.cancel-btn, .save-btn { min-height: 40px; padding: 0 17px; border-radius: 9px; font-size: 12px; font-weight: 800; }
.cancel-btn { border: 1px solid #dcd6e7; background: #fff; color: #655c70; }
.save-btn { border: 1px solid #6d28d9; background: #6d28d9; color: #fff; display: inline-flex; align-items: center; gap: 7px; }
.form-error { margin-bottom: 15px; padding: 10px 12px; border: 1px solid #efc8d0; border-radius: 8px; background: #fff3f5; color: #a83449; font-size: 12px; display: flex; gap: 8px; }
@keyframes mapping-shimmer { to { background-position: -200% 0; } }

/* Child component content rendered in this component's scope. */
::v-deep .mapping-tags { min-width: 105px; display: flex; align-items: flex-start; flex-wrap: wrap; gap: 4px; }
::v-deep .mapping-tag { max-width: 155px; padding: 3px 6px; overflow: hidden; border: 1px solid #dfd4f1; border-radius: 5px; background: #f5f0fc; color: #5f398e; font-size: 10px; line-height: 1.35; text-overflow: ellipsis; white-space: nowrap; }
::v-deep .tone-danger .mapping-tag { border-color: #f0d2d7; background: #fff2f4; color: #a13b4e; }
::v-deep .more-tags { padding: 3px 4px; border: 0; background: transparent; color: #6d28d9; font-size: 10px; font-weight: 800; }
::v-deep .no-values { color: #aaa3b2; }
::v-deep .mapping-manager-modal { overflow: hidden; border: 1px solid #ded8e9; border-radius: 14px; box-shadow: 0 24px 70px rgba(37, 23, 58, .25); }
::v-deep .mapping-manager-body { padding: 0; }
::v-deep .mapping-manager-shell .modal-dialog { max-width: min(1480px, 96vw); }

@media (max-width: 900px) {
  .manager-toolbar { grid-template-columns: 1fr 1fr; }
  .manager-search { grid-column: 1 / -1; }
  .add-mapping-btn { min-height: 42px; }
}
@media (max-width: 600px) {
  .manager-layout { min-height: 90vh; }
  .manager-header { padding: 18px 16px 14px; }
  .manager-header h3 { font-size: 20px; }
  .manager-toolbar { grid-template-columns: 1fr; padding: 13px 16px; }
  .manager-search { grid-column: auto; }
  .category-tabs { padding: 0 16px 10px; }
  .manager-pagination { padding: 10px 16px; align-items: flex-start; flex-direction: column; }
  .form-grid-two { grid-template-columns: 1fr; }
}
</style>
